import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';

// Canadian provinces and territories data
const provinces = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' }
];

enum TeamOption {
  INDIVIDUAL = 'individual',
  JOIN_TEAM = 'join_team',
  CREATE_TEAM = 'create_team'
}

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [province, setProvince] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Team options
  const [teamOption, setTeamOption] = useState<TeamOption>(TeamOption.INDIVIDUAL);
  const [teamName, setTeamName] = useState('');
  const [teamId, setTeamId] = useState<string | null>(null);
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [teamSearchResults, setTeamSearchResults] = useState<any[]>([]);
  const [showTeamResults, setShowTeamResults] = useState(false);
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchingTeams, setSearchingTeams] = useState(false);
  
  // Password strength indicators
  const getPasswordStrength = (password: string) => {
    if (!password) return 0;
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };
  
  const passwordStrength = getPasswordStrength(password);
  
  // Auto-search teams when query changes
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (teamSearchQuery.trim().length >= 2) {
        searchTeams();
      }
    }, 300);
    
    return () => clearTimeout(delaySearch);
  }, [teamSearchQuery]);
  
  // Close team results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#team-search-container')) {
        setShowTeamResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Search teams
  const searchTeams = async () => {
    if (!teamSearchQuery.trim() || teamSearchQuery.trim().length < 2) return;
    
    setSearchingTeams(true);
    try {
      const teamsRef = collection(db, 'teams');
      const q = query(
        teamsRef, 
        where('name', '>=', teamSearchQuery.toLowerCase()), 
        where('name', '<=', teamSearchQuery.toLowerCase() + '\uf8ff')
      );
      const querySnapshot = await getDocs(q);
      
      const teams: any[] = [];
      querySnapshot.forEach((doc) => {
        const teamData = doc.data();
        // Convert province code to full name
        const provinceName = provinces.find(p => p.code === teamData.province)?.name || teamData.province;
        teams.push({ 
          id: doc.id, 
          ...teamData,
          provinceDisplay: provinceName 
        });
      });
      
      setTeamSearchResults(teams);
      setShowTeamResults(true);
    } catch (error) {
      console.error('Error searching teams:', error);
    } finally {
      setSearchingTeams(false);
    }
  };
  
  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }
    
    if (passwordStrength < 3) {
      return setError('Password is not strong enough');
    }
    
    try {
      setLoading(true);
      
      // Prepare user data
      const userData = {
        firstName,
        lastName,
        province,
        teamId: null as string | null,
        teamName: ''
      };
      
      // Set team data based on option
      if (teamOption === TeamOption.JOIN_TEAM) {
        if (!teamId) {
          return setError('Please select a team to join');
        }
        userData.teamId = teamId;
      } else if (teamOption === TeamOption.CREATE_TEAM) {
        if (!teamName.trim()) {
          return setError('Please enter a team name');
        }
        userData.teamName = teamName;
      }
      
      // Register user
      await register(email, password, userData);
      
      // Redirect to miles logging page
      navigate('/log-miles');
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.message || 'Failed to create an account');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-bold text-text">Create your account</h2>
        <p className="mt-2 text-center text-sm text-text-secondary">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary hover:text-primary-dark transition-colors">
            Log in
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface py-8 px-4 shadow-card sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-error p-4 rounded">
              <p className="text-error">{error}</p>
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Personal Information */}
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-text">
                  First Name
                </label>
                <div className="mt-1">
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-text">
                  Last Name
                </label>
                <div className="mt-1">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="province" className="block text-sm font-medium text-text">
                Province/Territory
              </label>
              <div className="mt-1">
                <select
                  id="province"
                  name="province"
                  required
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                >
                  <option value="">Select Province/Territory</option>
                  {provinces.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Account Information */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                />
                {password && (
                  <div className="mt-2">
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <div
                          key={level}
                          className={`h-2 w-1/5 mx-0.5 rounded-sm ${
                            level <= passwordStrength
                              ? level <= 2
                                ? 'bg-red-400'
                                : level <= 3
                                ? 'bg-yellow-400'
                                : 'bg-green-400'
                              : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-text-secondary">
                      {passwordStrength <= 2
                        ? 'Weak password'
                        : passwordStrength <= 3
                        ? 'Medium password'
                        : 'Strong password'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-text">
                Confirm Password
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>

            {/* Team Selection */}
            <div>
              <label className="block text-sm font-medium text-text mb-2">Team Selection</label>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    id="teamOption-individual"
                    name="teamOption"
                    type="radio"
                    value={TeamOption.INDIVIDUAL}
                    checked={teamOption === TeamOption.INDIVIDUAL}
                    onChange={() => setTeamOption(TeamOption.INDIVIDUAL)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                  />
                  <label htmlFor="teamOption-individual" className="ml-3 block text-sm font-medium text-text">
                    Track your personal progress
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    id="teamOption-join"
                    name="teamOption"
                    type="radio"
                    value={TeamOption.JOIN_TEAM}
                    checked={teamOption === TeamOption.JOIN_TEAM}
                    onChange={() => setTeamOption(TeamOption.JOIN_TEAM)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                  />
                  <label htmlFor="teamOption-join" className="ml-3 block text-sm font-medium text-text">
                    Join existing team
                  </label>
                </div>
                
                {teamOption === TeamOption.JOIN_TEAM && (
                  <div className="ml-7 mt-3">
                    <div id="team-search-container" className="relative">
                      <input
                        type="text"
                        placeholder="Search for a team"
                        value={teamSearchQuery}
                        onChange={(e) => setTeamSearchQuery(e.target.value)}
                        onFocus={() => teamSearchResults.length > 0 && setShowTeamResults(true)}
                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      />
                      
                      {teamId && (
                        <div className="mt-2 p-2 border border-gray-300 rounded-md bg-gray-50">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-sm">{teamSearchQuery}</span>
                      <button
                        type="button"
                              className="text-xs text-red-500 hover:text-red-700"
                              onClick={() => {
                                setTeamId(null);
                                setTeamSearchQuery('');
                              }}
                            >
                              Clear
                      </button>
                    </div>
                        </div>
                      )}
                      
                      {searchingTeams && (
                        <div className="absolute right-3 top-2.5">
                          <svg className="animate-spin h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      )}
                      
                      {showTeamResults && teamSearchResults.length > 0 && !teamId && (
                        <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                        <ul className="divide-y divide-gray-200">
                          {teamSearchResults.map((team) => (
                              <li 
                                key={team.id} 
                                className="p-3 hover:bg-gray-50 cursor-pointer transition-colors" 
                                onClick={() => {
                              setTeamId(team.id);
                              setTeamSearchQuery(team.name);
                                  setShowTeamResults(false);
                                }}
                              >
                              <div className="flex justify-between">
                                <span className="font-medium">{team.name}</span>
                                <span className="text-sm text-gray-500">{team.provinceDisplay}</span>
                              </div>
                              <p className="text-xs text-gray-500">Members: {team.memberCount}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                      {showTeamResults && teamSearchResults.length === 0 && teamSearchQuery.length >= 2 && !searchingTeams && !teamId && (
                        <div className="absolute z-10 mt-1 w-full p-3 rounded-md border border-gray-300 bg-white shadow-lg">
                          <p className="text-sm text-gray-500">No teams found. Try a different search term.</p>
                        </div>
                    )}
                    </div>
                  </div>
                )}
                
                <div className="flex items-center">
                  <input
                    id="teamOption-create"
                    name="teamOption"
                    type="radio"
                    value={TeamOption.CREATE_TEAM}
                    checked={teamOption === TeamOption.CREATE_TEAM}
                    onChange={() => setTeamOption(TeamOption.CREATE_TEAM)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                  />
                  <label htmlFor="teamOption-create" className="ml-3 block text-sm font-medium text-text">
                    Create new team
                  </label>
                </div>
                
                {teamOption === TeamOption.CREATE_TEAM && (
                  <div className="ml-7 mt-3">
                    <input
                      type="text"
                      placeholder="Enter team name"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-text-secondary">
                      Your team will be associated with your province ({province ? (provinces.find(p => p.code === province)?.name || province) : 'not selected'}).
                    </p>
                  </div>
                )}
                
                {teamOption === TeamOption.INDIVIDUAL && (
                  <div className="mt-3 bg-yellow-50 p-3 rounded-md border border-yellow-100">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> If you begin as an individual and later wish to join a team, you will need to re-register with a new account to join the team of your choice.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-button text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register; 